import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { NIL as NIL_UUID } from 'uuid';
import { DrawerInputConfig } from '../../../models/drawer-model';
import { RouteService } from '../../../service/route.service';
import { API_ENDPOINTS } from '../../shared/constants/apiEnpoints';
import { DrawerComponent } from '../../shared/drawer/drawer.component';
import { DynamicGridComponent } from '../../shared/dynamic-grid/dynamic-grid.component';
import { GridConfig } from '../../shared/dynamic-grid/grid-model/grid-config.model';
import { Toast, ToastComponent } from '../../shared/toast/toast.component';
import { HttpClient } from '@angular/common/http';

export interface DentrixAdjustmentTypeData {
  id: string;
  practiceId: string;
  practiceName: string;
  defId: number;
  description: string;
  adjustmentType: string;
  isActive: boolean;
}

@Component({
  selector: 'app-dentrix-adjustment-type',
  templateUrl: './dentrix-adjustment-type.component.html',
  styleUrl: './dentrix-adjustment-type.component.scss'
})
export class DentrixAdjustmentTypeComponent {
  @ViewChild(DrawerComponent) drawerComponent!: DrawerComponent<DentrixAdjustmentTypeData>;
  @ViewChild(DynamicGridComponent) dynamicGrid!: DynamicGridComponent;
  @ViewChild(ToastComponent) toastComponent!: ToastComponent;

  userRole = localStorage.getItem('userRole');
  routes: string[] = [];
  private _routerSubscription: Subscription;

  gridConfig: GridConfig = {
    columns: [
      { label: 'Active', jsonKey: 'isActive', dataType: 'toggle', isSortable: false },
      { label: 'Practice Code', jsonKey: 'practiceCode', dataType: 'string', width: 'full' },
      { label: 'Code', jsonKey: 'defId', dataType: 'number' },
      { label: 'Description', jsonKey: 'description', dataType: 'string', width: 'full' },
      { label: 'Adjustment Type', jsonKey: 'adjustmentType', dataType: 'string' },
      { label: 'Created By', jsonKey: 'createdByUserEmail', dataType: 'string', hideOnToggle: true },
      { label: 'Created At', jsonKey: 'createdAt', dataType: 'date', hideOnToggle: true },
      { label: 'Modified By', jsonKey: 'modifiedByUserEmail', dataType: 'string', hideOnToggle: true },
      { label: 'Modified At', jsonKey: 'modifiedAt', dataType: 'date', hideOnToggle: true },
    ],
    filters: [
      {
        label: 'Practice',
        jsonKey: 'practiceId',
        type: 'multi',
        options: [],
        width: 'w-50',
      },
    ],
    editable: true,
    deletable: false,
    exportable: true,
    displayAdd: true,
    isToggleDisabled: true,
    defaultFilters: { isActive: true },
    uniqueTableName: 'DentrixAdjustmentType',
    displayHideAuditColumnsToggle: true,
  };

  drawerInputConfig: DrawerInputConfig[] = [
    {
      dataType: 'string',
      jsonKey: 'practiceName',
      label: 'Practice Code',
      input: 'select',
      dropDownOptionsURL: API_ENDPOINTS.GETALLACTIVEPRACTICES,
      dropdownJsonKey: 'practiceNameWithPMS',
      isRequired: true,
      dropdownSortDirection: 'asc',
      foreignKeyProperty: 'practiceId',
      isDropDownSearchable: true,
      uniqueIdentifier: 'DentrixPracticeCode'
    },
    {
      dataType: 'select',
      input: 'select',
      jsonKey: 'adjustmentType',
      label: 'Adjustment Type',
      isRequired: true,
      uniqueIdentifier: 'DentrixAdjustmentTypeOption',
      options: [
        { id: 'PRODUCTION_ADJUSTMENT', name: 'PRODUCTION_ADJUSTMENT' },
        { id: 'COLLECTION_ADJUSTMENT', name: 'COLLECTION_ADJUSTMENT' },
      ],
      dropdownJsonKey: 'name',
      foreignKeyProperty: 'adjustmentType'
    },
    {
      label: 'Code',
      jsonKey: 'defId',
      input: 'number',
      dataType: 'number',
      isRequired: true,
      minLength: 1,
      maxLength: 999999999
    },
    {
      label: 'Description',
      jsonKey: 'description',
      input: 'text',
      dataType: 'string',
      maxLength: 1000
    },
    {
      label: 'Active',
      jsonKey: 'isActive',
      input: 'toggle',
      dataType: 'boolean'
    },
  ];

  drawerModel: DentrixAdjustmentTypeData = {
    id: NIL_UUID,
    practiceId: '',
    practiceName: 'Select Practice',
    defId: null,
    description: '',
    adjustmentType: 'Select Type',
    isActive: true,
  };

  getDataUrl = API_ENDPOINTS.DENTRIX_ADJUSTMENT_TYPE;
  addDataUrl = API_ENDPOINTS.DENTRIX_ADJUSTMENT_TYPE;
  editDataUrl = API_ENDPOINTS.DENTRIX_ADJUSTMENT_TYPE;
  toggleUrl = API_ENDPOINTS.TOGGLE_DENTRIX_ADJUSTMENT_TYPE_ACTIVE;
  getPracticesUrl = API_ENDPOINTS.GETALLACTIVEPRACTICES;

  constructor(
    private routeService: RouteService,
    private cdr: ChangeDetectorRef,
    private _http: HttpClient
  ) { }

  ngOnInit() {
    this.fetchPracticeFilterOptions();
  }

  ngAfterViewInit(): void {
    this._routerSubscription = this.routeService.getRoutes().subscribe((data) => {
      this.routes = data;
      this.routes.shift();
      if (this.routes.length == 2) {
        this.routeService.redirectTO(this.routes.join('/').concat('/dentrix-adjustment-types'));
      }
      this.cdr.detectChanges();
    });
  }

  onAddHandler(): void {
    if (this.drawerComponent) {
      this.drawerComponent.drawerType = 'add';
      this.drawerInputConfig.find(c => c.jsonKey === 'practiceName').isDisabled = false;
      this.resetEntry();
      this.drawerComponent.openDrawer();
    }
  }

  onEditHandler(rowData: DentrixAdjustmentTypeData): void {
    if (this.drawerComponent) {
      this.drawerComponent.drawerType = 'edit';
      this.drawerInputConfig.find(c => c.jsonKey === 'practiceName').isDisabled = true;
      this.drawerModel = { ...rowData };
      this.drawerComponent.drawerModel = this.drawerModel;
      this.drawerComponent.openDrawer();
    }
  }

  resetEntry() {
    this.drawerModel = {
      id: NIL_UUID,
      practiceId: '',
      practiceName: 'Select Practice',
      defId: null,
      description: '',
      adjustmentType: 'Select Type',
      isActive: true,
    };
    this.drawerComponent.drawerModel = this.drawerModel;
  }

  refreshHandler() {
    this.dynamicGrid.fetchData();
  }

  fetchPracticeFilterOptions() {
    this._http.get<any>(this.getPracticesUrl).subscribe((response) => {
      response.data = response.data
        .map((option) => ({ ...option, name: option.practiceCode }))
        .sort((a, b) => a.name.localeCompare(b.name));
      response.data = [{ id: 'All', name: 'All Practices', checked: true },
         ...response.data.filter((option) => option.pmsName === 'Dentrix')];
      this.gridConfig.filters.find((f) => f.jsonKey === 'practiceId').options = response;
    });
  }

  showToast(toast: Toast) {
    this.toastComponent.showToast(toast);
    this.refreshHandler();
  }

  ngOnDestroy() {
    this._routerSubscription?.unsubscribe();
  }
}
